import { cx } from '../utils/cx.js';
import { Avatar, type AvatarPresence } from './Avatar.js';
import styles from './AvatarFacepile.module.css';

export type AvatarFacepileItem = {
  label: string;
  src?: string | null;
  /** Corner status on this face. */
  presence?: AvatarPresence;
  userId?: string;
};

export type AvatarFacepileProps = {
  items: AvatarFacepileItem[];
  /** Visual size of the pile (matches Avatar density). */
  size?: 'sm' | 'md';
  /** Max faces shown before +N. */
  max?: number;
  className?: string;
  /** Accessible name for the pile (defaults to joined labels). */
  label?: string;
};

export function AvatarFacepile({
  items,
  size = 'sm',
  max = 3,
  className,
  label,
}: AvatarFacepileProps) {
  const shown = items.slice(0, Math.max(1, max));
  const extra = items.length - shown.length;
  const aria =
    label?.trim() ||
    shown
      .map((item) => item.label)
      .filter(Boolean)
      .join(', ') ||
    'Groupe';

  if (!shown.length) return null;

  return (
    <span
      className={cx(styles.root, styles[size], className)}
      role="img"
      aria-label={aria}
    >
      {shown.map((item, index) => (
        <Avatar
          key={item.userId ?? `${item.label}-${index}`}
          label={item.label}
          src={item.src}
          size={size === 'md' ? 'md' : 'sm'}
          presence={item.presence}
          className={styles.face}
        />
      ))}
      {extra > 0 ? (
        <span className={styles.more} aria-hidden>
          +{extra}
        </span>
      ) : null}
    </span>
  );
}
